import Avatar from '@mui/material/Avatar';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useSnackbar } from 'notistack';
import { useState } from 'react';

import {
  usePartnersReadMembers,
  usePartnersRemoveMember,
  usePartnersRevokeMemberInvite,
} from '@/api/hooks';
import { type PartnerAccountResponse } from '@/api/routes/partners/partners.api';
import {
  type PartnerMember,
  type PartnerMemberInvite,
} from '@/api/routes/partners/partners.members.api';
import Button from '@/components/Button';
import Chip from '@/components/Chip';
import ConfirmModal from '@/components/ConfirmModal';
import { IconTrash } from '@/components/icon/tabler/IconTrash';
import { IconUsers } from '@/components/icon/tabler/IconUsers';
import { IconUsersPlus } from '@/components/icon/tabler/IconUsersPlus';
import { IconX } from '@/components/icon/tabler/IconX';
import AccountListSkeleton from '@/domain/account/components/AccountListSkeleton';
import InviteMemberModal from '@/domain/account/components/InviteMemberModal';
import {
  canManageMembers,
  canRemoveMember,
  formatAccountDate,
  getMemberInitials,
  getMemberName,
  memberRoleLabel,
} from '@/domain/account/utils';
import { useTranslate } from '@/state/i18n';
import { controlTokens, radiusTokens } from '@/theme/themePrimitives';

interface Props {
  account: PartnerAccountResponse;
}

type PendingAction =
  { kind: 'remove'; member: PartnerMember } | { kind: 'revoke'; invite: PartnerMemberInvite };

/** P15 · who has access and the open invites. The owner manages them once the account is approved. */
const AccountMembersCard = ({ account }: Props) => {
  const t = useTranslate('domain.account.components.AccountMembersCard');
  const { enqueueSnackbar } = useSnackbar();
  const { data, isLoading, isError, refetch } = usePartnersReadMembers();
  const { mutateAsync: removeMember, isPending: isRemoving } = usePartnersRemoveMember();
  const { mutateAsync: revokeInvite, isPending: isRevoking } = usePartnersRevokeMemberInvite();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [pending, setPending] = useState<PendingAction | null>(null);
  const manages = canManageMembers(account);
  const isOwner = account.role === 'owner';

  const handleConfirm = async () => {
    if (!pending) return;
    try {
      if (pending.kind === 'remove') {
        await removeMember(pending.member.userId);
        enqueueSnackbar(t('removed'), { variant: 'success' });
      } else {
        await revokeInvite(pending.invite.id);
        enqueueSnackbar(t('revoked'), { variant: 'success' });
      }
      setPending(null);
    } catch {
      // The API message goes to the global snackbar; the modal stays for another try.
    }
  };

  return (
    <Paper variant="outlined" sx={sx.paper}>
      <Stack direction="row" alignItems="center" gap={1}>
        <IconUsers sx={{ fontSize: 20, color: 'text.secondary' }} />
        <Typography variant="subtitle2" fontWeight={700}>
          {t('title')}
        </Typography>
      </Stack>

      {isLoading ? <AccountListSkeleton rows={2} /> : null}
      {isError ? (
        <Typography variant="body2" color="text.secondary">
          {t('loadError')}{' '}
          <Link component="button" type="button" onClick={() => refetch()}>
            {t('retry')}
          </Link>
        </Typography>
      ) : null}

      {data ? (
        <Stack component="ul" gap={1.25} sx={sx.list}>
          {data.members.map((member) => (
            <Stack component="li" key={member.userId} direction="row" alignItems="center" gap={1.5}>
              <Avatar sx={sx.avatar}>{getMemberInitials(member)}</Avatar>
              <Stack minWidth={0} flex={1}>
                <Typography variant="body2" fontWeight={600} noWrap>
                  {getMemberName(member)}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {member.email}
                </Typography>
              </Stack>
              <Chip
                size="xsmall"
                label={memberRoleLabel[member.role]}
                color={member.role === 'owner' ? 'dark' : 'neutral'}
                variant={member.role === 'owner' ? 'solid' : 'outline'}
              />
              {manages && canRemoveMember(member) ? (
                <Tooltip title={t('remove')}>
                  <IconButton
                    size="small"
                    aria-label={t('removeLabel', { name: getMemberName(member) })}
                    onClick={() => setPending({ kind: 'remove', member })}
                  >
                    <IconTrash fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : null}
            </Stack>
          ))}
          {data.invites.map((invite) => (
            <Stack component="li" key={invite.id} direction="row" alignItems="center" gap={1.5}>
              <Avatar sx={sx.pendingAvatar}>@</Avatar>
              <Stack minWidth={0} flex={1}>
                <Typography variant="body2" fontWeight={600} noWrap>
                  {invite.email}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {t('invitePending', {
                    role: memberRoleLabel[invite.role],
                    date: formatAccountDate(invite.expiresAt),
                  })}
                </Typography>
              </Stack>
              {manages ? (
                <Tooltip title={t('revoke')}>
                  <IconButton
                    size="small"
                    aria-label={t('revokeLabel', { email: invite.email })}
                    onClick={() => setPending({ kind: 'revoke', invite })}
                  >
                    <IconX fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : null}
            </Stack>
          ))}
        </Stack>
      ) : null}

      {manages ? (
        <Button
          variant="outlined"
          fullWidth
          startIcon={<IconUsersPlus />}
          onClick={() => setInviteOpen(true)}
        >
          {t('invite')}
        </Button>
      ) : (
        <Typography variant="caption" color="text.secondary">
          {t(isOwner ? 'inviteAfterApproval' : 'ownerOnly')}
        </Typography>
      )}

      <InviteMemberModal open={inviteOpen} onClose={() => setInviteOpen(false)} />
      <ConfirmModal
        open={Boolean(pending)}
        title={t(pending?.kind === 'revoke' ? 'revokeTitle' : 'removeTitle')}
        message={
          pending?.kind === 'revoke'
            ? t('revokeMessage', { email: pending.invite.email })
            : t('removeMessage', { name: pending ? getMemberName(pending.member) : '' })
        }
        confirmLabel={t(pending?.kind === 'revoke' ? 'revokeConfirm' : 'removeConfirm')}
        cancelLabel={t('cancel')}
        destructive
        loading={isRemoving || isRevoking}
        onConfirm={handleConfirm}
        onClose={() => setPending(null)}
      />
    </Paper>
  );
};

export default AccountMembersCard;

const sx = {
  paper: {
    borderRadius: radiusTokens.xxl,
    p: 2.5,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  list: { m: 0, p: 0, listStyle: 'none' },
  avatar: {
    width: 36,
    height: 36,
    fontSize: 13,
    fontWeight: 600,
    bgcolor: controlTokens.primary,
    color: 'common.white',
  },
  pendingAvatar: {
    width: 36,
    height: 36,
    fontSize: 14,
    bgcolor: 'transparent',
    color: 'text.secondary',
    border: 1,
    borderColor: 'divider',
    borderStyle: 'dashed',
  },
};
